import { Component,
  Input,
  OnInit,
  Output,
  EventEmitter
} from '@angular/core';
import OffenseRecord from '../../models/offenseRecord';
import {
  FormBuilder,
  FormGroup,
  Validators
} from '@angular/forms';
import FormControl from '../../models/formControl';
import { ChargeLevel } from '../../models/chargeLevels';
import Charge from '../../models/charge';
import { ChargeType } from '../../models/chargeType';
import DateHandler from '../../models/dateHandler';
import { SentenceLevel } from '../../models/sentenceLevel';
import isNumber from '../../validators/isNumberValidator';
import isDateValidator from '../../validators/isDateValidator';

@Component({
  selector: 'app-offense-counts',
  templateUrl: './offenseCounts.component.html',
  styleUrls: ['./offenseCounts.component.scss'],
  providers: []
})

export default class OffenseCountsComponent implements OnInit {

  constructor(private formBuilder: FormBuilder) { }

  ngOnInit(): void {
    this.offenseCountsPageForm = this.formBuilder.group({
      count: ['', [
        Validators.required,
        isNumber()
        ]
      ],
      chargeLevel: ['',  Validators.required],
      offense: ['', [
        Validators.required,
        Validators.maxLength(1000)
        ]
      ],
      disposition: ['', [
        Validators.required,
        Validators.maxLength(2000)
        ]
      ],
      chargeStatute: ['', Validators.maxLength(50) ],
      probation: ['', Validators.maxLength(255) ],
      fines: ['', Validators.maxLength(255) ],
      courtCosts: ['', Validators.maxLength(255) ],
      jailTime: ['', Validators.maxLength(255) ],
      prisonTime: ['', Validators.maxLength(255) ],
      suspensionTime: ['', Validators.maxLength(255) ],
      nextCourtDate: ['', isDateValidator(2000)],
      offenseDateGreaterThanCurrentDate: [''],
      fileDateGreaterThanCurrentDate: [''],
      arrestDateGreaterThanCurrentDate: [''],
      dispositionDateGreaterThanCurrentDate: [''],
      fileDateDateOlderThanOffenseDate: [''],
      dispositionDateOlderThanOffenseDate: [''],
      dispositionDateOlderThanFileDate: [''],
      offenseDate: ['', isDateValidator(2000)],
      noOffenseDateReason: [''],
      fileDate: ['', isDateValidator(2000)],
      noFileDateReason: [''],
      arrestDate: ['', isDateValidator(2000)],
      noArrestDateReason: [''],
      dispositionDate: ['', isDateValidator(2000)],
      noDispositionReason: [''],
      otherSentenceNotes: ['', Validators.maxLength(5000)],
    });
    this.offenseCountFormControl = new FormControl(this.offenseCountsPageForm);
  }

  @Input() set offenseRecordObject(offenseRecord: OffenseRecord) {
    if (offenseRecord) {
      this.offenseRecord = offenseRecord;
      this.createChargeOptions();
      this.offenseCountsPageForm.reset();
    }
  }

  @Input() set sentenceLevelObject(sentenceLevel: SentenceLevel) {
    if (this.offenseCountFormControl) {
      this.isRecord = +sentenceLevel === SentenceLevel.Record;
      if (this.isRecord) {
        this.offenseCountFormControl.disableControlByControlName('probation');
        this.offenseCountFormControl.disableControlByControlName('fines');
        this.offenseCountFormControl.disableControlByControlName('courtCosts');
        this.offenseCountFormControl.disableControlByControlName('jailTime');
        this.offenseCountFormControl.disableControlByControlName('prisonTime');
        this.offenseCountFormControl.disableControlByControlName('suspensionTime');
        this.offenseCountFormControl.disableControlByControlName('nextCourtDate');
        this.offenseCountFormControl.disableControlByControlName('otherSentenceNotes');
      } else {
        this.offenseCountFormControl.enableControlByControlName('probation');
        this.offenseCountFormControl.enableControlByControlName('fines');
        this.offenseCountFormControl.enableControlByControlName('courtCosts');
        this.offenseCountFormControl.enableControlByControlName('jailTime');
        this.offenseCountFormControl.enableControlByControlName('prisonTime');
        this.offenseCountFormControl.enableControlByControlName('suspensionTime');
        this.offenseCountFormControl.enableControlByControlName('nextCourtDate');
        this.offenseCountFormControl.enableControlByControlName('otherSentenceNotes');
      }
    }
  }

  @Output() updateOffenseDates = new EventEmitter<any>();
  @Output() updateToCurrentRecordCharges = new EventEmitter();

  runValidation() {
    this.validateDates();
  }

  formatDate(controlName: string) {
    if (!this.offenseCountFormControl.hasErrorByControlName(controlName)) {
      const value = this.offenseCountFormControl.getValueByControlName(controlName);
      this.offenseCountFormControl.setValueByControlName(controlName, this.convertToDateFormat(value));
    }

    this.validateDates();
  }

  addCharge(chargeType: ChargeType) {
    const charge = this.createChargeFromChargeUiControls(chargeType);

    if (chargeType === ChargeType.Convicted) {
      this.convictedChargeOptions = this.convictedChargeOptions.concat(this.getFieldsForCharge(charge));
    }  else if (chargeType === ChargeType.OtherNonConviction) {
      this.otherNonConvictionChargeOptions = this.otherNonConvictionChargeOptions.concat(this.getFieldsForCharge(charge));
    } else if (chargeType === ChargeType.Pending) {
      this.pendingChargeOptions = this.pendingChargeOptions.concat(this.getFieldsForCharge(charge));
    }  else if (chargeType === ChargeType.CompleteDeferral) {
      this.completeDeferralChargeOptions = this.completeDeferralChargeOptions.concat(this.getFieldsForCharge(charge));
    } else if (chargeType === ChargeType.ActiveDeferral) {
      this.activeDeferralChargeOptions = this.activeDeferralChargeOptions.concat(this.getFieldsForCharge(charge));
    }

    this.addChargeToOffense(charge);
    this.updateOffenseDatesWithGreatestDates();
    this.offenseCountsPageForm.reset();
    this.chargesHasChanged();
  }

  clearChargeFields() {
    this.offenseCountsPageForm.reset();
  }

  updateCharge() {
    const newCharge = this.createChargeFromChargeUiControls();

    const fieldOptions: any = this.getAllChargesOptions()
      .filter(cf => cf.selected);

    fieldOptions[0].charge.count = newCharge.count;
    fieldOptions[0].charge.chargeLevel = newCharge.chargeLevel;
    fieldOptions[0].charge.offense = newCharge.offense;
    fieldOptions[0].charge.disposition = newCharge.disposition;
    fieldOptions[0].charge.chargeStatute = newCharge.chargeStatute;
    fieldOptions[0].charge.probation = newCharge.probation;
    fieldOptions[0].charge.courtCost = newCharge.courtCost;
    fieldOptions[0].charge.jailTime = newCharge.jailTime;
    fieldOptions[0].charge.prisonTime = newCharge.prisonTime;
    fieldOptions[0].charge.suspensionTime = newCharge.suspensionTime;
    fieldOptions[0].charge.nextCourtDate = newCharge.nextCourtDate;
    fieldOptions[0].charge.offenseDate = newCharge.offenseDate;
    fieldOptions[0].charge.noOffenseDateReason = newCharge.noOffenseDateReason;
    fieldOptions[0].charge.fileDate = newCharge.fileDate;
    fieldOptions[0].charge.noFileDateReason = newCharge.noFileDateReason;
    fieldOptions[0].charge.arrestDate = newCharge.arrestDate;
    fieldOptions[0].charge.noArrestDateReason = newCharge.noArrestDateReason;
    fieldOptions[0].charge.dispositionDate = newCharge.dispositionDate;
    fieldOptions[0].charge.noDispositionReason = newCharge.noDispositionReason;
    fieldOptions[0].charge.otherSentenceNotes = newCharge.otherSentenceNotes;

    this.updateOffenseDatesWithGreatestDates();
    this.updateOptionFieldsForCharge(fieldOptions, fieldOptions[0].charge);
    this.offenseCountsPageForm.markAsPristine();
    this.chargesHasChanged();
  }

  deleteCharge() {
    const chargeFields = this.getAllChargesOptions().filter(c => c.selected);
    const chargeType = chargeFields[0].charge.chargeType;

    if (chargeType === ChargeType.Convicted) {
      this.removeChargesByIndex(this.convictedChargeOptions, chargeFields);
    }  else if (chargeType === ChargeType.OtherNonConviction) {
      this.removeChargesByIndex(this.otherNonConvictionChargeOptions, chargeFields);
    } else if (chargeType === ChargeType.Pending) {
      this.removeChargesByIndex(this.pendingChargeOptions, chargeFields);
    }  else if (chargeType === ChargeType.CompleteDeferral) {
      this.removeChargesByIndex(this.completeDeferralChargeOptions, chargeFields);
    } else if (chargeType === ChargeType.ActiveDeferral) {
      this.removeChargesByIndex(this.activeDeferralChargeOptions, chargeFields);
    }

    const charge = chargeFields[0].charge as Charge;
    this.removeChargeFromOffense(charge);
    this.updateOffenseDatesWithGreatestDates();
    this.offenseCountsPageForm.reset();
    this.chargesHasChanged();
  }

  selectCharge(chargeType: ChargeType, index: string) {
    this.offenseCountsPageForm.reset();
    const allChargeFields = this.getAllChargesOptions();
    const chargeFields = allChargeFields
      .filter(c => c.charge.chargeType === chargeType && c.index.toString() === index);

    for (const c of allChargeFields) {
      c.selected = false;
    }

    for (const c of chargeFields) {
      c.selected = true;
    }

    this.showSelectedCharge(chargeFields);
  }

  get isChargeDirty() {
    return this.hasSelectedCharge &&
      (this.offenseCountFormControl.isControlNameDirty('count')
      || this.offenseCountFormControl.isControlNameDirty('chargeLevel')
      || this.offenseCountFormControl.isControlNameDirty('offense')
      || this.offenseCountFormControl.isControlNameDirty('disposition')
      || this.offenseCountFormControl.isControlNameDirty('chargeStatute')
      || this.offenseCountFormControl.isControlNameDirty('probation')
      || this.offenseCountFormControl.isControlNameDirty('fines')
      || this.offenseCountFormControl.isControlNameDirty('courtCosts')
      || this.offenseCountFormControl.isControlNameDirty('jailTime')
      || this.offenseCountFormControl.isControlNameDirty('prisonTime')
      || this.offenseCountFormControl.isControlNameDirty('suspensionTime')
      || this.offenseCountFormControl.isControlNameDirty('nextCourtDate')
      || this.offenseCountFormControl.isControlNameDirty('offenseDate')
      || this.offenseCountFormControl.isControlNameDirty('noOffenseDateReason')
      || this.offenseCountFormControl.isControlNameDirty('fileDate')
      || this.offenseCountFormControl.isControlNameDirty('noFileDateReason')
      || this.offenseCountFormControl.isControlNameDirty('arrestDate')
      || this.offenseCountFormControl.isControlNameDirty('noArrestDateReason')
      || this.offenseCountFormControl.isControlNameDirty('dispositionDate')
      || this.offenseCountFormControl.isControlNameDirty('noDispositionReason')
      || this.offenseCountFormControl.isControlNameDirty('otherSentenceNotes'));
  }

  get count() { return this.offenseCountsPageForm.get('count'); }
  get chargeLevel() { return this.offenseCountsPageForm.get('chargeLevel'); }
  get offense() { return this.offenseCountsPageForm.get('offense'); }
  get disposition() { return this.offenseCountsPageForm.get('disposition'); }
  get chargeStatute() { return this.offenseCountsPageForm.get('chargeStatute'); }
  get probation() { return this.offenseCountsPageForm.get('probation'); }
  get fines() { return this.offenseCountsPageForm.get('fines'); }
  get courtCosts() { return this.offenseCountsPageForm.get('courtCosts'); }
  get jailTime() { return this.offenseCountsPageForm.get('jailTime'); }
  get prisonTime() { return this.offenseCountsPageForm.get('prisonTime'); }
  get suspensionTime() { return this.offenseCountsPageForm.get('suspensionTime'); }
  get nextCourtDate() { return this.offenseCountsPageForm.get('nextCourtDate'); }
  get OffenseDateGreaterThanCurrentDate() { return this.offenseCountsPageForm.get('offenseDateGreaterThanCurrentDate'); }
  get fileDateGreaterThanCurrentDate() { return this.offenseCountsPageForm.get('fileDateGreaterThanCurrentDate'); }
  get arrestDateGreaterThanCurrentDate() { return this.offenseCountsPageForm.get('arrestDateGreaterThanCurrentDate'); }
  get dispositionDateGreaterThanCurrentDate() { return this.offenseCountsPageForm.get('dispositionDateGreaterThanCurrentDate'); }
  get fileDateDateOlderThanOffenseDate() { return this.offenseCountsPageForm.get('fileDateDateOlderThanOffenseDate'); }
  get dispositionDateOlderThanOffenseDate() { return this.offenseCountsPageForm.get('dispositionDateOlderThanOffenseDate'); }
  get dispositionDateOlderThanFileDate() { return this.offenseCountsPageForm.get('dispositionDateOlderThanFileDate'); }
  get offenseDate() { return this.offenseCountsPageForm.get('offenseDate'); }
  get noOffenseDateReason() { return this.offenseCountsPageForm.get('noOffenseDateReason'); }
  get fileDate() { return this.offenseCountsPageForm.get('fileDate'); }
  get noFileDateReason() { return this.offenseCountsPageForm.get('noFileDateReason'); }
  get arrestDate() { return this.offenseCountsPageForm.get('arrestDate'); }
  get noArrestDateReason() { return this.offenseCountsPageForm.get('noArrestDateReason'); }
  get dispositionDate() { return this.offenseCountsPageForm.get('dispositionDate'); }
  get noDispositionReason() { return this.offenseCountsPageForm.get('noDispositionReason'); }
  get otherSentenceNotes() { return this.offenseCountsPageForm.get('otherSentenceNotes'); }


  private chargesHasChanged() {
    this.updateToCurrentRecordCharges.emit();
  }

  private validateDates() {
    const offenseDate = this.offenseCountFormControl.getDateByControlName('offenseDate');
    const fileDate = this.offenseCountFormControl.getDateByControlName('fileDate');
    const arrestDate = this.offenseCountFormControl.getDateByControlName('arrestDate');
    const dispositionDate = this.offenseCountFormControl.getDateByControlName('dispositionDate');

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (offenseDate != null && offenseDate > today) {
      this.offenseCountFormControl.setValidationErrorByControlName('offenseDateGreaterThanCurrentDate', 'greaterThanCurrentDate');
    } else {
      this.offenseCountFormControl.clearValidatorErrorByControlName('offenseDateGreaterThanCurrentDate', 'greaterThanCurrentDate');
    }

    if (fileDate != null && fileDate > today) {
      this.offenseCountFormControl.setValidationErrorByControlName('fileDateGreaterThanCurrentDate', 'greaterThanCurrentDate');
    } else {
      this.offenseCountFormControl.clearValidatorErrorByControlName('fileDateGreaterThanCurrentDate', 'greaterThanCurrentDate');
    }

    if (fileDate != null && fileDate > today) {
      this.offenseCountFormControl.setValidationErrorByControlName('arrestDateGreaterThanCurrentDate', 'greaterThanCurrentDate');
    } else {
      this.offenseCountFormControl.clearValidatorErrorByControlName('arrestDateGreaterThanCurrentDate', 'greaterThanCurrentDate');
    }

    if (arrestDate !== null && arrestDate > today) {
      this.offenseCountFormControl.setValidationErrorByControlName('arrestDateGreaterThanCurrentDate', 'greaterThanCurrentDate');
    } else {
      this.offenseCountFormControl.clearValidatorErrorByControlName('arrestDateGreaterThanCurrentDate', 'greaterThanCurrentDate');
    }

    if (dispositionDate != null && dispositionDate > today) {
      this.offenseCountFormControl.setValidationErrorByControlName('dispositionDateGreaterThanCurrentDate', 'greaterThanCurrentDate');
    } else {
      this.offenseCountFormControl.clearValidatorErrorByControlName('dispositionDateGreaterThanCurrentDate', 'greaterThanCurrentDate');
    }

    if (offenseDate !== null && fileDate !== null && offenseDate > fileDate) {
      this.offenseCountFormControl.setValidationErrorByControlName('fileDateDateOlderThanOffenseDate', 'olderThan');
    } else {
      this.offenseCountFormControl.clearValidatorErrorByControlName('fileDateDateOlderThanOffenseDate', 'olderThan');
    }

    if (offenseDate !== null && dispositionDate !== null && offenseDate > dispositionDate) {
      this.offenseCountFormControl.setValidationErrorByControlName('dispositionDateOlderThanOffenseDate', 'olderThan');
    } else {
      this.offenseCountFormControl.clearValidatorErrorByControlName('dispositionDateOlderThanOffenseDate', 'olderThan');
    }

    if (fileDate !== null && dispositionDate !== null && fileDate > dispositionDate) {
      this.offenseCountFormControl.setValidationErrorByControlName('dispositionDateOlderThanFileDate', 'olderThan');
    } else {
      this.offenseCountFormControl.clearValidatorErrorByControlName('dispositionDateOlderThanFileDate', 'olderThan');
    }
  }

  private get hasSelectedCharge() {
    return this.getAllChargesOptions()
      .some(cf => cf.selected);
  }

  private createChargeFromChargeUiControls(chargeType: ChargeType = null) {
    const count = this.offenseCountFormControl.getNumberByControlName('count');
    const chargeLevel = this.offenseCountFormControl.getNumberByControlName('chargeLevel');
    const offense = this.offenseCountFormControl.getValueByControlName('offense');
    const disposition = this.offenseCountFormControl.getValueByControlName('disposition');
    const chargeStatute = this.offenseCountFormControl.getValueByControlName('chargeStatute');
    const probation = this.offenseCountFormControl.getValueByControlName('probation');
    const fines = this.offenseCountFormControl.getValueByControlName('fines');
    const courtCosts = this.offenseCountFormControl.getValueByControlName('courtCosts');
    const jailTime = this.offenseCountFormControl.getValueByControlName('jailTime');
    const prisonTime = this.offenseCountFormControl.getValueByControlName('prisonTime');
    const suspensionTime = this.offenseCountFormControl.getValueByControlName('suspensionTime');
    const nextCourtDate = this.offenseCountFormControl.getDateByControlName('nextCourtDate');
    const offenseDate = this.offenseCountFormControl.getDateByControlName('offenseDate');
    const noOffenseDateReason = this.offenseCountFormControl.getValueByControlName('noOffenseDateReason');
    const fileDate = this.offenseCountFormControl.getDateByControlName('fileDate');
    const noFileDateReason = this.offenseCountFormControl.getValueByControlName('noFileDateReason');
    const arrestDate = this.offenseCountFormControl.getDateByControlName('arrestDate');
    const noArrestDateReason = this.offenseCountFormControl.getValueByControlName('noArrestDateReason');
    const dispositionDate = this.offenseCountFormControl.getDateByControlName('dispositionDate');
    const noDispositionReason = this.offenseCountFormControl.getValueByControlName('noDispositionReason');
    const otherSentenceNotes = this.offenseCountFormControl.getValueByControlName('otherSentenceNotes');

    if (chargeType == null) {
      chargeType = this.getAllChargesOptions()
        .find(cf => cf.selected).charge.chargeType;
    }

    return Charge.createCharge(this.offenseRecord.requestNumber,
      this.offenseRecord.offenseNumber,
      count, chargeLevel,
      offense, disposition, chargeStatute,
      probation, fines, courtCosts,
      jailTime, prisonTime, suspensionTime,
      nextCourtDate == null ? null : new Date(nextCourtDate),
      offenseDate == null ? null : new Date(offenseDate),
      noOffenseDateReason,
      fileDate == null ? null : new Date(fileDate),
      noFileDateReason,
      arrestDate == null ? null : new Date(arrestDate),
      noArrestDateReason,
      dispositionDate == null ? null : new Date(dispositionDate),
      noDispositionReason,
      otherSentenceNotes, chargeType);
  }

  private updateOffenseDatesWithGreatestDates() {

    const offenseDates = this.offenseRecord.charges
      .filter(c => c != null)
      .map(c => c.offenseDate);

    const fileDates = this.offenseRecord.charges
      .filter(c => c != null)
      .map(c => c.fileDate);

    const arrestDates = this.offenseRecord.charges
      .filter(c => c != null)
      .map(c => c.arrestDate);

    const dispositionDates = this.offenseRecord.charges
      .filter(c => c != null)
      .map(c => c.dispositionDate);

    let offenseDate: Date = null;
    let fileDate: Date = null;
    let arrestDate: Date = null;
    let dispositionDate: Date = null;

    if (offenseDates.length > 0) {
      offenseDate = offenseDates.reduce((a, b) => a > b ? a : b);
    }

    if (fileDates.length > 0) {
      fileDate = fileDates.reduce((a, b) => a > b ? a : b);
    }

    if (arrestDates.length > 0) {
      arrestDate = arrestDates.reduce((a, b) => a > b ? a : b);
    }

    if (dispositionDates.length > 0) {
      dispositionDate = dispositionDates.reduce((a, b) => a > b ? a : b);
    }

    this.updateOffenseDates.emit({
      offenseDate: offenseDate == null ? null : new DateHandler(offenseDate).format(),
      fileDate: fileDate == null ? null : new DateHandler(fileDate).format(),
      arrestDate: arrestDate == null ? null : new DateHandler(arrestDate).format(),
      dispositionDate: dispositionDate == null ? null : new DateHandler(dispositionDate).format()
    });
  }

  private removeChargesByIndex(chargeFields: any[], deletedCharges: any[]) {
    const firstIndex = chargeFields.findIndex(cf => cf.index === deletedCharges[0].index);
    const lastIndex = chargeFields.lastIndexOf(deletedCharges[deletedCharges.length - 1]);
    chargeFields.splice(firstIndex, lastIndex - firstIndex + 1);
  }

  private removeChargeFromOffense(charge: Charge) {
    if (charge) {
      const index = this.offenseRecord.charges.findIndex(c => c === charge);
      this.offenseRecord.charges.splice(index, 1);
    }
  }

  private addChargeToOffense(charge: Charge) {
    this.offenseRecord.charges.push(charge);
  }

  private showSelectedCharge(chargeFields: any[]) {
    this.offenseCountFormControl.setValueByControlName('count', this.getFieldFromString(chargeFields[0].description));
    this.offenseCountFormControl.setValueByControlName('chargeLevel',
      ChargeLevel[this.removeSpaces(this.getFieldFromString(chargeFields[1].description))]);
    this.offenseCountFormControl.setValueByControlName('offense', this.getFieldFromString(chargeFields[2].description));
    this.offenseCountFormControl.setValueByControlName('disposition', this.getFieldFromString(chargeFields[3].description));
    this.offenseCountFormControl.setValueByControlName('offenseDate', this.getFieldFromString(chargeFields[4].description));
    this.offenseCountFormControl.setValueByControlName('fileDate', this.getFieldFromString(chargeFields[5].description));
    this.offenseCountFormControl.setValueByControlName('arrestDate', this.getFieldFromString(chargeFields[6].description));
    this.offenseCountFormControl.setValueByControlName('dispositionDate', this.getFieldFromString(chargeFields[7].description));
    this.offenseCountFormControl.setValueByControlName('probation', this.getFieldFromString(chargeFields[8].description));
    this.offenseCountFormControl.setValueByControlName('fines', this.getFieldFromString(chargeFields[9].description));
    this.offenseCountFormControl.setValueByControlName('courtCosts', this.getFieldFromString(chargeFields[10].description));
    this.offenseCountFormControl.setValueByControlName('jailTime', this.getFieldFromString(chargeFields[11].description));
    this.offenseCountFormControl.setValueByControlName('prisonTime', this.getFieldFromString(chargeFields[12].description));
    this.offenseCountFormControl.setValueByControlName('suspensionTime', this.getFieldFromString(chargeFields[13].description));
    this.offenseCountFormControl.setValueByControlName('nextCourtDate', this.getFieldFromString(chargeFields[14].description));
    this.offenseCountFormControl.setValueByControlName('otherSentenceNotes', this.getFieldFromString(chargeFields[15].description));
    this.offenseCountFormControl.setValueByControlName('noDispositionReason', this.getFieldFromString(chargeFields[16].description));
    this.offenseCountFormControl.setValueByControlName('noArrestDateReason', this.getFieldFromString(chargeFields[17].description));
    this.offenseCountFormControl.setValueByControlName('noFileDateReason', this.getFieldFromString(chargeFields[18].description));
    this.offenseCountFormControl.setValueByControlName('noOffenseDateReason', this.getFieldFromString(chargeFields[19].description));
    this.offenseCountFormControl.setValueByControlName('chargeStatute', this.getFieldFromString(chargeFields[20].description));

  }

  private getFieldFromString(description: string) {
    return description.substring(description.indexOf(': ') + 2);
  }

  private camelCaseToString(s: string): string {
      return s.split(/(?=[A-Z])/).join(' ');
  }

  private removeSpaces(s: string): string {
    return s.replace(' ', '');
  }

  private setOddAndEventClass(charges: any) {

    const uniqueIndexes = charges.map(c => c.index).filter((value, index, self) => {
      return self.indexOf(value) === index;
    });

    let odd = true;
    for (const index of uniqueIndexes) {
      const indexGroupCharge = charges.filter(c => c.index === index);
      for (const charge of indexGroupCharge) {
        charge.cssClass = odd ? 'odd-background-charge' : 'even-background-charge';
      }
      odd = !odd;
    }

    return charges;
  }

  private createChargeOptions() {
    if (this.chargeIndex > 0) {
      this.chargeIndex = 0;
      this.convictedChargeOptions.length = 0;
      this.otherNonConvictionChargeOptions.length = 0;
      this.pendingChargeOptions.length = 0;
      this.completeDeferralChargeOptions.length = 0;
      this.completeDeferralChargeOptions.length = 0;
    }

    this.convictedChargeOptions = this.getChargesByChargeType(ChargeType.Convicted);
    this.otherNonConvictionChargeOptions = this.getChargesByChargeType(ChargeType.OtherNonConviction);
    this.pendingChargeOptions = this.getChargesByChargeType(ChargeType.Pending);
    this.completeDeferralChargeOptions = this.getChargesByChargeType(ChargeType.CompleteDeferral);
    this.activeDeferralChargeOptions = this.getChargesByChargeType(ChargeType.ActiveDeferral);
  }

  private getAllChargesOptions(): any[] {
    let charges = this.convictedChargeOptions.concat(this.activeDeferralChargeOptions);
    charges = charges.concat(this.completeDeferralChargeOptions);
    charges = charges.concat(this.otherNonConvictionChargeOptions);
    charges = charges.concat(this.pendingChargeOptions);
    return charges;
  }

  private getChargesByChargeType(chargeType: ChargeType) {
    const charges = this.hasCharges
    ? this.offenseRecord.charges.filter(c => c.chargeType === chargeType)
    : new Array<Charge>();
    return this.setOddAndEventClass(this.getFieldsForCharges(charges));
  }


  private getFieldsForCharges(charges: Charge[]): any[] {

    let options = new Array<any>();
    for (const charge of charges) {
      options = options.concat(this.getFieldsForCharge(charge));
    }

    return options;
  }

  private createChargeOption(description: string, charge: Charge, index: number) {
    return {
      class: '',
      selectedCssClass: 'selected-charge',
      selected: false,
      description: description,
      charge: charge,
      index: index
    };
  }

  private get hasCharges(): boolean {
    return this.offenseRecord !== null && this.offenseRecord !== undefined
    && this.offenseRecord.charges !== null && this.offenseRecord.charges !== undefined
    && this.offenseRecord.charges.length > 0;
  }

  private updateOptionFieldsForCharge(fieldOptions: any[], charge: Charge) {
    fieldOptions[0].description = `Count: ${charge.count}`;
    fieldOptions[1].description = `Level: ${this.camelCaseToString(ChargeLevel[charge.chargeLevel])}`;
    fieldOptions[2].description = `Offense: ${charge.offense}`;
    fieldOptions[3].description = `Disposition: ${charge.disposition}`;
    fieldOptions[4].description = `Offense Date: ${charge.offenseDate == null ? '' : this.convertToDateFormat(charge.offenseDate)}`;
    fieldOptions[5].description = `File Date: ${charge.fileDate == null ? '' : this.convertToDateFormat(charge.fileDate)}`;
    fieldOptions[6].description = `Arrest Date: ${charge.arrestDate == null ? '' : this.convertToDateFormat(charge.arrestDate)}`;
    fieldOptions[7].description = `Disposition Date: ${charge.dispositionDate == null
        ? ''
        : this.convertToDateFormat(charge.dispositionDate)}`;
    fieldOptions[8].description = `Probation: ${charge.probation == null ? '' : charge.probation}`;
    fieldOptions[9].description = `Fines: ${charge.fines == null ? '' : charge.fines}`;
    fieldOptions[10].description = `Court Costs: ${charge.courtCost == null ? '' : charge.courtCost}`;
    fieldOptions[11].description = `Jail Time: ${charge.jailTime == null ? '' : charge.jailTime}`;
    fieldOptions[12].description = `Prison Time: ${charge.prisonTime == null ? '' : charge.prisonTime}`;
    fieldOptions[13].description = `Suspension Time: ${charge.suspensionTime == null ? '' : charge.suspensionTime}`;
    fieldOptions[14].description = `Next Court Date: ${charge.nextCourtDate == null ? '' : this.convertToDateFormat(charge.nextCourtDate)}`;
    fieldOptions[15].description = `Other Sentencing Notes: ${charge.otherSentenceNotes == null ? '' : charge.otherSentenceNotes}`;
    fieldOptions[16].description = `NoDispDateReason: ${charge.noDispositionReason == null ? '' : charge.noDispositionReason}`;
    fieldOptions[17].description = `NoArrestDateReason: ${charge.noFileDateReason == null ? '' : charge.noArrestDateReason}`;
    fieldOptions[18].description = `NoFileDateReason: ${charge.noFileDateReason == null ? '' : charge.noFileDateReason}`;
    fieldOptions[19].description = `NoOffenseDateReason: ${charge.noOffenseDateReason == null ? '' : charge.noOffenseDateReason}`;
    fieldOptions[20].description = `Charge Statute: ${charge.chargeStatute == null ? '' : charge.chargeStatute}`;
  }

  private getFieldsForCharge(charge: Charge): any[] {
    const options = new Array<any>();
    const chargeIndex = this.getNewChargeIndex();
    options.push(this.createChargeOption(`Count: ${charge.count}`, charge, chargeIndex));
    options.push(this.createChargeOption(`Level: ${this.camelCaseToString(ChargeLevel[charge.chargeLevel])}`, charge, chargeIndex));
    options.push(this.createChargeOption(`Offense: ${charge.offense}`, charge, chargeIndex));
    options.push(this.createChargeOption(`Disposition: ${charge.disposition}`, charge, chargeIndex));
    options.push(this.createChargeOption(`Offense Date: ${charge.offenseDate == null
      ? ''
      : this.convertToDateFormat(charge.offenseDate)}`, charge, chargeIndex));
    options.push(this.createChargeOption(`File Date: ${charge.fileDate == null
      ? ''
      : this.convertToDateFormat(charge.fileDate)}`, charge, chargeIndex));
    options.push(this.createChargeOption(`Arrest Date: ${charge.arrestDate == null
      ? ''
      : this.convertToDateFormat(charge.arrestDate)}`, charge, chargeIndex));
    options.push(this.createChargeOption(`Disposition Date: ${charge.dispositionDate == null
      ? ''
      : this.convertToDateFormat(charge.dispositionDate)}`, charge, chargeIndex));
    options.push(this.createChargeOption(`Probation: ${charge.probation == null ? '' : charge.probation}`, charge, chargeIndex));
    options.push(this.createChargeOption(`Fines: ${charge.fines == null ? '' : charge.fines}`, charge, chargeIndex));
    options.push(this.createChargeOption(`Court Costs: ${charge.courtCost == null ? '' : charge.courtCost}`, charge, chargeIndex));
    options.push(this.createChargeOption(`Jail Time: ${charge.jailTime == null ? '' : charge.jailTime}`, charge, chargeIndex));
    options.push(this.createChargeOption(`Prison Time: ${charge.prisonTime == null ? '' : charge.prisonTime}`, charge, chargeIndex));
    options.push(this.createChargeOption(`Suspension Time: ${charge.suspensionTime == null
      ? ''
      : charge.suspensionTime}`, charge, chargeIndex));
    options.push(this.createChargeOption(`Next Court Date: ${charge.nextCourtDate == null
      ? ''
      : this.convertToDateFormat(charge.nextCourtDate)}`, charge, chargeIndex));
    options.push(this.createChargeOption(`Other Sentencing Notes: ${charge.otherSentenceNotes == null
      ? ''
      : charge.otherSentenceNotes}`, charge, chargeIndex));
    options.push(this.createChargeOption(`NoDispDateReason: ${charge.noDispositionReason == null
      ? ''
      : charge.noDispositionReason}`, charge, chargeIndex));
    options.push(this.createChargeOption(`NoArrestDateReason: ${charge.noArrestDateReason == null
      ? ''
      : charge.noArrestDateReason}`, charge, chargeIndex));
    options.push(this.createChargeOption(`NoFileDateReason: ${charge.noFileDateReason == null
      ? ''
      : charge.noFileDateReason}`, charge, chargeIndex));
    options.push(this.createChargeOption(`NoOffenseDateReason: ${charge.noOffenseDateReason == null
      ? ''
      : charge.noOffenseDateReason}`, charge, chargeIndex));
    options.push(this.createChargeOption(`Charge Statute: ${charge.chargeStatute == null
      ? ''
      : charge.chargeStatute}`, charge, chargeIndex));
    return options;
  }

  private convertToDateFormat(date: any, baseCentury: number = 2000) {
    return new DateHandler(date, baseCentury).format();
  }

  private getNewChargeIndex() {
    return this.chargeIndex++;
  }

  convictedChargeOptions: any[];
  activeDeferralChargeOptions: any[];
  completeDeferralChargeOptions: any[];
  otherNonConvictionChargeOptions: any[];
  pendingChargeOptions: any[];

  private chargeIndex = 0;
  private offenseRecord: OffenseRecord;
  public offenseCountFormControl: FormControl;
  offenseCountsPageForm: FormGroup;
  isRecord = false;

 }



// WEBPACK FOOTER //
// ./src/app/offenseCounts/offenseCounts.component.ts